// Centralized configuration for the SignalCue web application.
//
// Values come from window._env_, which docker-entrypoint.sh writes into
// /usr/share/nginx/html/env-config.js at CONTAINER START from the container's
// environment. index.html loads that file before the bundle.
//
// WHY NOT process.env.REACT_APP_*. Create React App inlines those at BUILD time,
// so changing an API URL meant rebuilding and repushing an image. It also caused
// deploy blocker #2: with REACT_APP_CORE_API_URL unset, a production bundle fell
// through to http://localhost:8095 and pointed the whole app at the viewer's own
// machine. Reading the container's environment at start removes both problems.
//
// The localhost fallbacks below are DEVELOPMENT ONLY, keyed on the browser's own
// hostname. Off localhost there is no localhost fallback at all -- an
// unconfigured deployed bundle gets an empty base URL and its fetches fail
// loudly against its own origin, which is a bug you can see, rather than a
// silent connection attempt against the user's laptop.

const env = (typeof window !== 'undefined' && window._env_) || {};
const hostname = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
const onLocalhost = ['localhost', '127.0.0.1'].includes(hostname);

// pick returns the runtime value, or the development fallback when we are on a
// developer's machine, or '' when a deployment simply did not configure it.
function pick(key, devFallback) {
  const value = env[key];
  if (typeof value === 'string' && value !== '') return value;
  return onLocalhost ? devFallback : '';
}

// API_BASE_URL is the legacy Python api. Nothing in web/ calls it after A9; it
// survives because production still runs that service and a future maintenance
// fix may need to address it. It has NO fallback off localhost -- 8090 is
// vacant on purpose, so a re-introduced call fails with connection refused
// rather than silently reaching production.
export const API_BASE_URL = pick('API_URL', 'http://localhost:8090');

// APP_URL and LOGIN_URL are navigation targets, not API hosts, and that makes
// pick()'s '' off localhost the WRONG default for them. CORE_API_URL is a host
// you cannot guess -- failing loudly (a broken fetch against '') beats a silent
// call to someone else's machine. A navigation target you CAN guess: it's
// wherever the browser already is. So these resolve to window.location.origin
// whenever window._env_ does not supply them, on localhost and off it alike --
// a single-host deployment then works with zero configuration, and only a
// two-host deployment (the marketing/login site at signalcue.ai vs the app at
// app.signalcue.ai, or their staging equivalents) needs to set them explicitly.
// Staying on the current host is always safe; guessing the OTHER host by string
// surgery on this one is not -- that used to be hardcoded at each of 13 call
// sites (`origin.replace('app.signalcue.ai', 'signalcue.ai')` and back), so it
// broke, and invented a host that does not exist, on any hostname those two
// literals do not appear in -- e.g. a staging domain.
function resolveOrigin(key) {
  const value = env[key];
  if (typeof value === 'string' && value !== '') return value;
  return typeof window !== 'undefined' && window.location ? window.location.origin : '';
}

export const APP_URL = resolveOrigin('APP_URL');
export const LOGIN_URL = resolveOrigin('LOGIN_URL');

// The Go data-pipeline control service (services/data-pipeline cmd/pipelinecontrol),
// served on :8092 by compose. The variable keeps the name PIPELINE_API_URL: it
// was already accurate and renaming it would touch every caller for no gain.
export const PIPELINE_API_URL = pick('PIPELINE_API_URL', 'http://localhost:8092');

// The Go backend (cmd/api): login, /me, logout, change-password, signup,
// verification, set-password, contact, support, the setup wizard, organisation
// settings, manage team, ATS integrations, candidate graph, outreach and signal
// sources. Compose serves it on :8095.
export const CORE_API_URL = pick('CORE_API_URL', 'http://localhost:8095');

// The Userback feedback widget's access token.
//
// NO LOCALHOST FALLBACK, deliberately, and that makes this the odd one out in
// this file. Every value above has a dev fallback because a developer wants the
// app to work with no configuration. Here the opposite is true: the safe state
// is OFF. An empty token means UserbackProvider never calls the SDK, so local,
// dev, production, Jest and Playwright all get no widget without any of them
// knowing Userback exists. Turning it on takes a deliberate act -- setting the
// variable -- in exactly one place, which is staging's Cloud Run service.
//
// It is NOT a secret. It is a public client-side credential present in the DOM
// of every page that loads the widget, which is why it travels as a Cloud Build
// substitution rather than through Secret Manager. Do not "harden" that.
export const USERBACK_TOKEN = pick('USERBACK_TOKEN', '');

// Which deployment this bundle is. nginx has read SIGNALCUE_ENV from the
// container environment since the staging work (it is what sends
// X-Robots-Tag: noindex off production), but the BUNDLE has never seen it.
// A bug report that cannot say which environment it came from costs a round
// trip to establish the first thing anyone asks.
export const SIGNALCUE_ENV = pick('SIGNALCUE_ENV', 'local');

// Which build this is. The deploy writes both into the web service's
// environment: APP_VERSION is the stg- tag when a tag triggered the deploy, and
// the short commit otherwise; APP_COMMIT is always the short commit. Empty means
// nobody set them -- a local compose build -- and the environment badge says so.
export const APP_VERSION = pick('APP_VERSION', '');
export const APP_COMMIT = pick('APP_COMMIT', '');

console.log('SignalCue config loaded:', {
  API_BASE_URL,
  APP_URL,
  LOGIN_URL,
  PIPELINE_API_URL,
  CORE_API_URL,
  SIGNALCUE_ENV,
  APP_VERSION,
  APP_COMMIT,
  // The token is public, but logging it is noise, and a value echoed in the
  // console is a value someone pastes into a bug report. Whether one is
  // configured is the only part that helps.
  USERBACK: USERBACK_TOKEN ? 'configured' : 'off',
  NODE_ENV: process.env.NODE_ENV,
});
